import { useContext, useMemo, useState } from "react";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { I18nextContext, Link } from "gatsby-plugin-react-i18next";

import useSearch from "../../utils/hooks/useSearch";
import ArrowButton from "../Atoms/ArrowButton";
import { SELLING_PRODUCT_VALORACION_ROUTE } from "../../utils/routes/const";

export type SearchResult = {
  id: string,
  referenceNumber: string,
  slug: string,
  brand: string,
  model: string,
  watchInfo: string,
  imageUrl: string,
  marketValueFrom: string | number,
  marketValueTo: string | number,
}

const SearchInput = ({ displayResults = true }: { displayResults: boolean }) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useSearch(searchQuery, isFocused);

  return (
    <div className="flex justify-between text-primary relative">
      <div className="w-full">
        <input
          className="bg-transparent w-full"
          placeholder={t('sellingHero.searchPlaceholder') || ''}
          name="q"
          data-test="selling-search-input"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
        />
        {displayResults &&
          <div className="absolute top-14.5 w-full" style={{ filter: 'drop-shadow(8px 12px 30px rgba(163, 141, 112, 0.267455))' }}>
            <ResultsMapper data={data} isLoading={isLoading} />
          </div>
        }
      </div>
      <button type="submit" className="flex items-center font-light" >
        <ArrowButton isGhost={false} className="ml-4">
          <span className="ml-3 hidden md:inline-block">{t('sellingHero.searchButton')}</span>
        </ArrowButton>
      </button>
    </div>
  )
}

export const ResultsMapper = ({ data, isLoading }: { data: Array<any>, isLoading: boolean }) => {
  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!data || data.length <= 3) {
    return null;
  }

  if (data && data['hydra:member'].length === 0) {
    return <div>No Search Results</div>
  }

  const localeContext = useContext(I18nextContext);
  const results: SearchResult[] = useMemo(() => data['hydra:member'].map((result: SearchResult) => {
    result.marketValueFrom = Intl.NumberFormat(localeContext.language, { style: 'currency', currency: 'EUR', currencyDisplay: 'symbol' }).format(result.marketValueFrom || 0);
    result.marketValueTo = Intl.NumberFormat(localeContext.language, { style: 'currency', currency: 'EUR', currencyDisplay: 'symbol' }).format(result.marketValueTo || 0);
    result.imageUrl = result.imageUrl || 'https://user-images.githubusercontent.com/24848110/33519396-7e56363c-d79d-11e7-969b-09782f5ccbab.png';

    return result;
  }), [data]);

  return <>
    {results.map((result: SearchResult) => (
      <Link to={SELLING_PRODUCT_VALORACION_ROUTE(result.slug.replace(/^\/|\/$/g, ''))} key={result.id} className="flex items-center bg-white border-b border-gray z-10 no-animation">
        <img src={result.imageUrl} alt={result.name} className="w-28 h-28 mr-5 object-contain" loading="lazy" />
        <div className="flex flex-col justify-around">
          <div className="text-lg text-primary font-cirka">{result.brand} {result.model} ({result.id})</div>
          <div className="text-sm text-gray-700 mt-1">{result.marketValueFrom} - {result.marketValueTo}</div>
          <div className="text-sm text-gray-700 mt-1">{result.watchInfo}</div>
        </div>
      </Link>
    ))}
  </>;
}

export default SearchInput;
